import React, { useRef, useEffect, useState } from 'react';
import {
  ChevronDown, MoreHorizontal, Edit, Trash2, FolderOpen, BookOpen,
  MoveRight, Check,
} from 'lucide-react';
import { getRyanIconComponent } from '../utils/ryanGroupIcons';
import { RyanSubdeckGroupData, RyanConfig, RyanTab } from '../types/ryan.types';
import { getRyanFieldColor } from '../constants/ryanFieldVisuals';
import RyanSubdeckCard from './RyanSubdeckCard';
import {
  DndContext, closestCenter, PointerSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, rectSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './RyanSubdeckGroupContainer.css';

interface SubdeckData {
  _id: string;
  name: string;
  description?: string;
  type: 'flashcards' | 'apostila';
  hasResumo?: boolean;
  cardCount: number;
  deckId: string;
  tabId: string | null;
  groupId?: string | null;
  displayCategoryId?: string | null;
  masteryPercentage: number | null;
  lastReviewedAt?: string | null;
  colorId?: string;
  iconId?: string | null;
  badge?: string | null;
  isBlocked?: boolean;
  isHidden?: boolean;
  excludeFromCount?: boolean;
  restricted?: boolean;
  restrictedMessage?: string;
  isFree?: boolean;
  hasRead?: boolean;
  pageCount?: number;
  // Venda granular: pode ser true mesmo com o `comprado` de topo (o campo
  // inteiro) false — ver comentário equivalente em RyanFieldPage.tsx.
  comprado?: boolean;
}

interface RyanSubdeckGroupContainerProps {
  group: RyanSubdeckGroupData | null;
  subdecks: SubdeckData[];
  isExpanded: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  spaceId: string;
  spaceName: string;
  isVip: boolean;
  isEditor: boolean;
  comprado: boolean;
  ryanConfig: RyanConfig;
  tabs?: RyanTab[];
  onEditGroup?: () => void;
  onDeleteGroup?: () => void;
  onMoveGroup?: (newTabId: string | null) => void;
  onReorderSubdecks?: (reordered: SubdeckData[]) => void;
  onStudyGroup?: () => void;
  onDelete: (subdeckId: string, deckId: string) => void;
  onExport: (deckId: string, subdeckId: string) => void;
  onExportApostila: (deckId: string, subdeckId: string, name: string) => void;
  onMove: (subdeck: SubdeckData) => void;
  onCopy: (subdeck: SubdeckData) => void;
  onMarkRead: (subdeck: SubdeckData) => void;
  onToggleBlock: (subdeck: SubdeckData) => void;
  onToggleHidden: (subdeck: SubdeckData) => void;
  onToggleExcludeCount: (subdeck: SubdeckData) => void;
  onToggleFree: (subdeck: SubdeckData) => void;
  onOpenRestrict: (subdeck: SubdeckData) => void;
  onRemoveRestriction: (subdeck: SubdeckData) => void;
  onEdit: (subdeck: SubdeckData) => void;
  onBlockedClick: (name: string) => void;
  onRestrictedClick: (subdeck: SubdeckData) => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
  onAssignGroup: (subdeck: SubdeckData) => void;
  onAssignCategory?: (subdeck: SubdeckData) => void;
  onConfigurePreview?: (subdeck: SubdeckData) => void;
  onConfigureApostilaPreview?: (subdeck: SubdeckData) => void;
  /** Seleção múltipla (mover subdecks entre abas em lote) */
  selectionMode?: boolean;
  selectedSubdeckIds?: Set<string>;
  onToggleSelect?: (subdeckId: string) => void;
}

const SortableGroupSubdeck: React.FC<{
  subdeck: SubdeckData;
  spaceId: string;
  spaceName: string;
  isAdmin: boolean;
  isVip: boolean;
  isEditor: boolean;
  comprado: boolean;
  ryanConfig: RyanConfig;
  onDelete: (subdeckId: string, deckId: string) => void;
  onExport: (deckId: string, subdeckId: string) => void;
  onExportApostila: (deckId: string, subdeckId: string, name: string) => void;
  onMove: (subdeck: SubdeckData) => void;
  onCopy: (subdeck: SubdeckData) => void;
  onMarkRead: (subdeck: SubdeckData) => void;
  onToggleBlock: (subdeck: SubdeckData) => void;
  onToggleHidden: (subdeck: SubdeckData) => void;
  onToggleExcludeCount: (subdeck: SubdeckData) => void;
  onToggleFree: (subdeck: SubdeckData) => void;
  onOpenRestrict: (subdeck: SubdeckData) => void;
  onRemoveRestriction: (subdeck: SubdeckData) => void;
  onEdit: (subdeck: SubdeckData) => void;
  onBlockedClick: (name: string) => void;
  onRestrictedClick: (subdeck: SubdeckData) => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
  onAssignGroup: (subdeck: SubdeckData) => void;
  onAssignCategory?: (subdeck: SubdeckData) => void;
  onConfigurePreview?: (subdeck: SubdeckData) => void;
  onConfigureApostilaPreview?: (subdeck: SubdeckData) => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (subdeckId: string) => void;
}> = ({ subdeck, spaceId, spaceName, isAdmin, isVip, isEditor, comprado, ryanConfig,
  onDelete, onExport, onExportApostila, onMove, onCopy, onMarkRead, onToggleBlock,
  onToggleHidden, onToggleExcludeCount, onToggleFree, onOpenRestrict, onRemoveRestriction, onEdit, onBlockedClick, onRestrictedClick,
  onToast, onAssignGroup, onAssignCategory, onConfigurePreview,
  onConfigureApostilaPreview, selectionMode = false, isSelected = false, onToggleSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: subdeck._id,
    disabled: !isAdmin || selectionMode,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    height: '100%',
  };
  return (
    <div ref={setNodeRef} style={style}>
      <RyanSubdeckCard
        subdeck={subdeck}
        spaceId={spaceId}
        spaceName={spaceName}
        isAdmin={isAdmin}
        isVip={isVip}
        isEditor={isEditor}
        comprado={subdeck.comprado ?? comprado}
        ryanConfig={ryanConfig}
        accentColor={getRyanFieldColor(subdeck.colorId)}
        dragHandleAttrs={isAdmin ? { attributes, listeners } : undefined}
        onDelete={() => onDelete(subdeck._id, subdeck.deckId)}
        onExport={() => onExport(subdeck.deckId, subdeck._id)}
        onExportApostila={() => onExportApostila(subdeck.deckId, subdeck._id, subdeck.name)}
        onMove={() => onMove(subdeck)}
        onCopy={() => onCopy(subdeck)}
        onMarkRead={() => onMarkRead(subdeck)}
        onToggleBlock={() => onToggleBlock(subdeck)}
        onToggleHidden={() => onToggleHidden(subdeck)}
        onToggleExcludeCount={() => onToggleExcludeCount(subdeck)}
        onToggleFree={() => onToggleFree(subdeck)}
        onOpenRestrict={() => onOpenRestrict(subdeck)}
        onRemoveRestriction={() => onRemoveRestriction(subdeck)}
        onEdit={() => onEdit(subdeck)}
        onBlockedClick={() => onBlockedClick(subdeck.name)}
        onRestrictedClick={() => onRestrictedClick(subdeck)}
        onAssignGroup={() => onAssignGroup(subdeck)}
        onAssignCategory={onAssignCategory ? () => onAssignCategory(subdeck) : undefined}
        onConfigurePreview={onConfigurePreview ? () => onConfigurePreview(subdeck) : undefined}
        onConfigureApostilaPreview={onConfigureApostilaPreview ? () => onConfigureApostilaPreview(subdeck) : undefined}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect ? () => onToggleSelect(subdeck._id) : undefined}
      />
    </div>
  );
};

const RyanSubdeckGroupContainer: React.FC<RyanSubdeckGroupContainerProps> = ({
  group, subdecks, isExpanded, onToggle,
  isAdmin, spaceId, spaceName, isVip, isEditor, comprado, ryanConfig,
  tabs,
  onEditGroup, onDeleteGroup, onMoveGroup, onReorderSubdecks, onStudyGroup,
  onDelete, onExport, onExportApostila, onMove, onCopy,
  onMarkRead, onToggleBlock, onToggleHidden, onToggleExcludeCount, onToggleFree, onOpenRestrict, onRemoveRestriction,
  onEdit, onBlockedClick, onRestrictedClick,
  onToast, onAssignGroup, onAssignCategory, onConfigurePreview, onConfigureApostilaPreview,
  selectionMode = false, selectedSubdeckIds, onToggleSelect,
}) => {
  const [kebabOpen, setKebabOpen] = useState(false);
  const [moveSubmenuOpen, setMoveSubmenuOpen] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) {
        setKebabOpen(false);
        setMoveSubmenuOpen(false);
      }
    };
    if (kebabOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [kebabOpen]);

  const isUngrouped = group === null;
  const color = isUngrouped ? '#9CA3AF' : group!.color;
  const name = isUngrouped ? 'Sem grupo' : group!.name;
  const description = isUngrouped ? undefined : group?.description;

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const groupTint   = hexToRgba(color, 0.04);
  const groupTint6  = hexToRgba(color, 0.06);
  const groupTint10 = hexToRgba(color, 0.10);

  const renderIcon = () => {
    if (isUngrouped) return <FolderOpen size={24} color="#9CA3AF" />;
    const IconComp = getRyanIconComponent(group!.iconName || 'Layers');
    return <IconComp size={26} color={color} />;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = subdecks.findIndex(s => s._id === active.id);
    const newIndex = subdecks.findIndex(s => s._id === over.id);
    onReorderSubdecks?.(arrayMove(subdecks, oldIndex, newIndex));
  };

  const hasFlashcards = subdecks.some(s => s.type !== 'apostila');

  return (
    <div
      className={`ryan-group-container${isExpanded ? ' ryan-group-container--open' : ''}${isUngrouped ? ' ryan-group-container--ungrouped' : ''}`}
      style={{
        '--group-color': color,
        '--group-tint': groupTint,
        '--group-tint-6': groupTint6,
        '--group-tint-10': groupTint10,
      } as React.CSSProperties}
    >
      <div className="ryan-group-header" onClick={onToggle}>
        <div className="ryan-group-icon-wrap">{renderIcon()}</div>

        <div className="ryan-group-text">
          <span className="ryan-group-name">{name}</span>
          {description && <span className="ryan-group-desc">{description}</span>}
        </div>

        <span className="ryan-group-counter">
          <span>{subdecks.length} deck{subdecks.length !== 1 ? 's' : ''}</span>
          <span className="ryan-group-counter-dot">•</span>
          <span>{subdecks.reduce((acc, s) => acc + (s.excludeFromCount ? 0 : (s.cardCount || 0)), 0)} cards</span>
        </span>

        {!isUngrouped && isAdmin && (
          <div
            className="ryan-group-kebab-wrap"
            ref={kebabRef}
            onClick={e => e.stopPropagation()}
          >
            <button
              className="ryan-group-kebab-btn"
              onClick={e => { e.stopPropagation(); setKebabOpen(p => !p); }}
              title="Opções do grupo"
            >
              <MoreHorizontal size={16} />
            </button>
            {kebabOpen && (
              <div className="ryan-group-kebab-menu">
                <button className="ryan-group-kebab-item" onClick={() => { setKebabOpen(false); setMoveSubmenuOpen(false); onEditGroup?.(); }}>
                  <Edit size={14} /> Editar grupo
                </button>

                {onMoveGroup && tabs && tabs.length > 0 && (
                  <>
                    <button
                      className="ryan-group-kebab-item"
                      onClick={e => { e.stopPropagation(); setMoveSubmenuOpen(p => !p); }}
                    >
                      <MoveRight size={14} /> Mover grupo
                      <ChevronDown
                        size={12}
                        style={{ marginLeft: 'auto', transform: moveSubmenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
                      />
                    </button>

                    {moveSubmenuOpen && (
                      <>
                        <div className="ryan-group-kebab-divider" />
                        <div className="ryan-group-move-header">Mover para aba</div>
                        {tabs.slice().sort((a, b) => a.order - b.order).map(tab => {
                          const isCurrent = group?.tabId === tab._id;
                          return (
                            <button
                              key={tab._id}
                              className={`ryan-group-move-tab-btn${isCurrent ? ' ryan-group-move-tab-btn--current' : ''}`}
                              onClick={() => {
                                setKebabOpen(false);
                                setMoveSubmenuOpen(false);
                                if (!isCurrent) onMoveGroup(tab._id);
                              }}
                            >
                              {tab.name}
                              {isCurrent && <Check size={12} style={{ marginLeft: 'auto' }} />}
                            </button>
                          );
                        })}
                        <div className="ryan-group-kebab-divider" />
                      </>
                    )}
                  </>
                )}

                <button className="ryan-group-kebab-item ryan-group-kebab-item--danger" onClick={() => { setKebabOpen(false); setMoveSubmenuOpen(false); onDeleteGroup?.(); }}>
                  <Trash2 size={14} /> Excluir grupo
                </button>
              </div>
            )}
          </div>
        )}

        {onStudyGroup && hasFlashcards && (
          <button
            className="ryan-group-study-btn"
            onClick={e => { e.stopPropagation(); onStudyGroup(); }}
            title="Estudar este grupo"
          >
            <BookOpen size={14} />
            <span>Estudar</span>
          </button>
        )}

        <ChevronDown
          size={18}
          className={`ryan-group-chevron${isExpanded ? ' ryan-group-chevron--open' : ''}`}
        />
      </div>

      <div className={`ryan-group-body${isExpanded ? ' expanded' : ''}`}>
        {subdecks.length === 0 ? (
          <div className="ryan-group-empty">Nenhum deck neste grupo.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={subdecks.map(s => s._id)} strategy={rectSortingStrategy}>
              <div className="ryan-subdecks-grid">
                {subdecks.map(subdeck => (
                  <SortableGroupSubdeck
                    key={subdeck._id}
                    subdeck={subdeck}
                    spaceId={spaceId}
                    spaceName={spaceName}
                    isAdmin={isAdmin}
                    isVip={isVip}
                    isEditor={isEditor}
                    comprado={comprado}
                    ryanConfig={ryanConfig}
                    onDelete={onDelete}
                    onExport={onExport}
                    onExportApostila={onExportApostila}
                    onMove={onMove}
                    onCopy={onCopy}
                    onMarkRead={onMarkRead}
                    onToggleBlock={onToggleBlock}
                    onToggleHidden={onToggleHidden}
                    onToggleExcludeCount={onToggleExcludeCount}
                    onToggleFree={onToggleFree}
                    onOpenRestrict={onOpenRestrict}
                    onRemoveRestriction={onRemoveRestriction}
                    onEdit={onEdit}
                    onBlockedClick={onBlockedClick}
                    onRestrictedClick={onRestrictedClick}
                    onToast={onToast}
                    onAssignGroup={onAssignGroup}
                    onAssignCategory={onAssignCategory}
                    onConfigurePreview={onConfigurePreview}
                    onConfigureApostilaPreview={onConfigureApostilaPreview}
                    selectionMode={selectionMode}
                    isSelected={!!selectedSubdeckIds?.has(subdeck._id)}
                    onToggleSelect={onToggleSelect}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default RyanSubdeckGroupContainer;
